/**
 * Contenido legal vigente — Terminos y Privacidad (admin).
 *
 * IMPORTANTE: este archivo es una copia 1:1 del que vive en
 * app/lib/legal/content.ts. Cuando bumpeés LEGAL_VERSION en uno,
 * actualizá el otro. v2 de esta integración debería extraer ambos a
 * un workspace package compartido (packages/legal/) — por ahora
 * preferimos duplicación a setup de package compartido.
 *
 * Fuente de verdad humana: docs/legal-v1-borradores.md.
 */

export const LEGAL_VERSION = '1.0'
export const LEGAL_EFFECTIVE_DATE = '2026-04-21'

export const COOPERATIVA_INFO = {
  fullName: 'Cooperativa Integral de Ahorro y Crédito NEO, Responsabilidad Limitada',
  shortName: 'NEOCOOP, R.L.',
  nit: '120719290',
  address:
    'El Cubo Center, Oficina 209, Km 77 Ruta Nacional 14, San Lorenzo El Cubo, Ciudad Vieja, Sacatepéquez, Guatemala',
  addressShort:
    'El Cubo Center, Oficina 209 · Ciudad Vieja, Sacatepéquez',
  email: 'info@neocoop.gt',
  privacyEmail: 'privacidad@neocoop.gt',
  phone: '+502 5865-8811',
  web: 'https://neocoop.gt',
} as const

export type Block =
  | { type: 'heading'; level: 1 | 2; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }

export type LegalDoc = {
  kind: 'terms' | 'privacy'
  title: string
  version: string
  effectiveDate: string
  intro: string
  blocks: Block[]
}

export const terms: LegalDoc = {
  kind: 'terms',
  title: 'Términos y Condiciones de Uso',
  version: LEGAL_VERSION,
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  intro:
    'Estos Términos rigen el uso de la app móvil de Neocoop. Al ingresar y usar la app, el Asociado declara que los acepta.',
  blocks: [
    { type: 'heading', level: 1, text: '1. Aceptación' },
    {
      type: 'paragraph',
      text: 'Al crear una cuenta, acceder o utilizar la aplicación móvil "neocoop." (en adelante, "la App"), el usuario (en adelante, "el Asociado") declara que ha leído, entendido y aceptado los presentes Términos y Condiciones de Uso (en adelante, "los Términos"). Si el Asociado no está de acuerdo con alguna parte, debe abstenerse de usar la App.',
    },

    { type: 'heading', level: 1, text: '2. Identidad del responsable' },
    {
      type: 'paragraph',
      text: `La App es operada por ${COOPERATIVA_INFO.fullName}, también conocida como ${COOPERATIVA_INFO.shortName} (en adelante, "la Cooperativa"), con NIT ${COOPERATIVA_INFO.nit}, con domicilio en ${COOPERATIVA_INFO.address}.`,
    },
    {
      type: 'paragraph',
      text: `Contacto: ${COOPERATIVA_INFO.email} · ${COOPERATIVA_INFO.phone} · ${COOPERATIVA_INFO.web}.`,
    },

    { type: 'heading', level: 1, text: '3. Naturaleza del servicio' },
    {
      type: 'paragraph',
      text: 'La App es una herramienta de consulta que permite al Asociado, en calidad de miembro de la Cooperativa, visualizar saldos, movimientos, estados de cuenta y otros datos asociados a sus productos vigentes con la Cooperativa. La App no es un servicio bancario ni financiero independiente: es un canal complementario al vínculo cooperativo que el Asociado ya tiene con la Cooperativa conforme a su estatuto y la Ley General de Cooperativas.',
    },

    { type: 'heading', level: 1, text: '4. Requisitos para el uso' },
    { type: 'paragraph', text: 'Para usar la App, el Asociado debe:' },
    {
      type: 'list',
      items: [
        'Ser Asociado registrado y activo de la Cooperativa.',
        'Tener capacidad legal para contratar conforme a la legislación guatemalteca.',
        'Contar con un dispositivo móvil compatible (iOS 16+ o Android 10+) y conexión a Internet.',
        'Aceptar los presentes Términos y la Política de Privacidad.',
      ],
    },

    { type: 'heading', level: 1, text: '5. Registro y credenciales' },
    {
      type: 'paragraph',
      text: 'El Asociado accede con su número de teléfono registrado y un PIN o contraseña personal. Opcionalmente, puede habilitar autenticación biométrica (huella o reconocimiento facial) en su dispositivo.',
    },
    {
      type: 'paragraph',
      text: 'El Asociado es el único responsable de mantener la confidencialidad de sus credenciales y de las operaciones realizadas desde sus sesiones. La Cooperativa no será responsable de los perjuicios derivados del uso no autorizado de las credenciales por terceros cuando medie negligencia del Asociado.',
    },
    {
      type: 'paragraph',
      text: 'En caso de pérdida, sospecha de uso indebido o robo del dispositivo, el Asociado debe notificar de inmediato a la Cooperativa por cualquiera de los canales de contacto.',
    },

    { type: 'heading', level: 1, text: '6. Uso aceptable' },
    { type: 'paragraph', text: 'El Asociado se compromete a:' },
    {
      type: 'list',
      items: [
        'Usar la App exclusivamente para sus fines personales como miembro de la Cooperativa.',
        'No intentar acceder a información de otros Asociados, realizar ingeniería inversa, descompilar, o vulnerar los mecanismos de seguridad de la App.',
        'No usar la App para actividades ilícitas, incluyendo lavado de dinero, financiamiento del terrorismo, fraude o suplantación.',
        'Mantener actualizados sus datos de contacto en los registros de la Cooperativa.',
      ],
    },
    {
      type: 'paragraph',
      text: 'El incumplimiento de estas obligaciones puede implicar la suspensión del acceso a la App y, según su gravedad, la aplicación de sanciones conforme a los estatutos de la Cooperativa y a la ley aplicable.',
    },

    { type: 'heading', level: 1, text: '7. Disponibilidad del servicio' },
    {
      type: 'paragraph',
      text: 'La Cooperativa procurará mantener la App operativa de forma continua, pero no garantiza disponibilidad 100% ininterrumpida. Podrá realizar mantenimientos programados o no programados, interrumpiendo total o parcialmente el servicio. En la medida de lo razonable, se notificarán los mantenimientos programados con antelación.',
    },
    {
      type: 'paragraph',
      text: 'Los saldos mostrados en la App corresponden a la última sincronización con los sistemas centrales de la Cooperativa. Aunque se actualizan con alta frecuencia, pueden existir desfases de minutos u horas respecto del saldo real del sistema central. La información de referencia oficial, en caso de discrepancia, es la que consta en los sistemas centrales de la Cooperativa, no la mostrada por la App.',
    },

    { type: 'heading', level: 1, text: '8. Operaciones' },
    {
      type: 'paragraph',
      text: 'En esta versión de la App, las funcionalidades disponibles son exclusivamente de consulta: visualización de saldos, movimientos, estados de cuenta y descarga de documentos. La App no permite realizar transferencias, retiros, depósitos ni otras operaciones financieras que modifiquen el saldo de las cuentas del Asociado. Funcionalidades transaccionales futuras se regirán por términos complementarios específicos.',
    },

    { type: 'heading', level: 1, text: '9. Notificaciones push' },
    {
      type: 'paragraph',
      text: 'La App puede enviar notificaciones al dispositivo del Asociado sobre movimientos en sus cuentas, avisos institucionales de la Cooperativa y actualizaciones de la App. El Asociado puede deshabilitar estas notificaciones desde el sistema operativo de su dispositivo o desde los ajustes de la App.',
    },

    { type: 'heading', level: 1, text: '10. Propiedad intelectual' },
    {
      type: 'paragraph',
      text: 'Todos los elementos de la App —código fuente, diseño, nombre comercial "neocoop.", logos, iconografía, textos, ilustraciones, sonidos— son propiedad de la Cooperativa o de sus licenciantes y están protegidos por la legislación de derechos de autor y propiedad industrial aplicable. El Asociado recibe una licencia limitada, personal, no transferible, no exclusiva y revocable para usar la App conforme a estos Términos.',
    },
    {
      type: 'paragraph',
      text: 'El uso no autorizado de elementos protegidos puede constituir infracción conforme a la Ley de Derecho de Autor y Derechos Conexos (Decreto 33-98) y demás normativa aplicable.',
    },

    { type: 'heading', level: 1, text: '11. Limitación de responsabilidad' },
    {
      type: 'paragraph',
      text: 'Dentro de los límites permitidos por la legislación aplicable:',
    },
    {
      type: 'list',
      items: [
        'La Cooperativa no será responsable por daños indirectos, incidentales, consecuentes o punitivos derivados del uso o imposibilidad de uso de la App.',
        'La Cooperativa no será responsable por fallas de terceros proveedores (Apple App Store, Google Play, proveedores de infraestructura cloud, operadores de telecomunicaciones) que afecten la disponibilidad de la App.',
        'La Cooperativa no renuncia a su responsabilidad por dolo, culpa grave, o por obligaciones que conforme a la ley son indisponibles.',
      ],
    },

    { type: 'heading', level: 1, text: '12. Modificaciones' },
    {
      type: 'paragraph',
      text: 'La Cooperativa podrá modificar estos Términos en cualquier momento. Las modificaciones sustantivas serán comunicadas al Asociado con al menos 15 días corridos de antelación, mediante aviso en la App y/o correo electrónico registrado. El uso continuado de la App después de la entrada en vigor de las modificaciones implica su aceptación. Si el Asociado no acepta los nuevos Términos, puede dejar de usar la App.',
    },

    { type: 'heading', level: 1, text: '13. Suspensión y terminación' },
    {
      type: 'paragraph',
      text: 'La Cooperativa podrá suspender o terminar el acceso del Asociado a la App si:',
    },
    {
      type: 'list',
      items: [
        'El Asociado incumple estos Términos o la Política de Privacidad.',
        'Existen sospechas fundadas de actividades ilícitas o fraudulentas.',
        'El Asociado deja de ser miembro de la Cooperativa.',
      ],
    },
    {
      type: 'paragraph',
      text: 'La terminación del acceso a la App no implica, por sí misma, la terminación del vínculo cooperativo, que se rige por los estatutos de la Cooperativa.',
    },

    { type: 'heading', level: 1, text: '14. Ley aplicable y jurisdicción' },
    {
      type: 'paragraph',
      text: 'Estos Términos se rigen por las leyes de la República de Guatemala. Cualquier controversia se someterá a los tribunales competentes de la ciudad de Guatemala, renunciando expresamente el Asociado a cualquier otro fuero que pudiera corresponderle.',
    },

    { type: 'heading', level: 1, text: '15. Contacto' },
    {
      type: 'paragraph',
      text: `Para cualquier consulta sobre estos Términos, el Asociado puede escribir a ${COOPERATIVA_INFO.email} o visitar las oficinas de la Cooperativa.`,
    },
  ],
}

export const privacy: LegalDoc = {
  kind: 'privacy',
  title: 'Política de Privacidad',
  version: LEGAL_VERSION,
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  intro:
    'Esta política describe qué datos personales recolectamos, para qué los usamos, con quién los compartimos y qué derechos tiene el Asociado sobre ellos.',
  blocks: [
    { type: 'heading', level: 1, text: '1. Responsable del tratamiento de datos' },
    {
      type: 'paragraph',
      text: `El responsable del tratamiento de los datos personales recolectados a través de la App es ${COOPERATIVA_INFO.fullName} (${COOPERATIVA_INFO.shortName}), con NIT ${COOPERATIVA_INFO.nit} y domicilio en ${COOPERATIVA_INFO.address}.`,
    },
    {
      type: 'paragraph',
      text: `Para ejercer los derechos descritos en esta política o realizar consultas sobre el tratamiento de datos, el Asociado puede contactar al oficial de protección de datos designado por la Cooperativa al correo ${COOPERATIVA_INFO.privacyEmail}.`,
    },

    { type: 'heading', level: 1, text: '2. Datos que recolectamos' },

    { type: 'heading', level: 2, text: 'Datos proporcionados directamente por el Asociado' },
    {
      type: 'list',
      items: [
        'Credenciales de acceso (teléfono registrado, PIN o contraseña cifrada).',
        'Enrollment biométrico (clave pública del dispositivo; la información biométrica nunca sale del dispositivo).',
        'Preferencias de la App (notificaciones, idioma, visibilidad de saldos).',
      ],
    },

    { type: 'heading', level: 2, text: 'Datos sincronizados desde los sistemas de la Cooperativa' },
    {
      type: 'list',
      items: [
        'Datos personales: nombre completo, DPI, género, fecha de nacimiento, profesión.',
        'Datos de contacto: dirección, municipio, departamento, teléfono, correo.',
        'Datos de productos: cuentas, saldos, movimientos, estados de cuenta.',
      ],
    },

    { type: 'heading', level: 2, text: 'Datos recolectados automáticamente al usar la App' },
    {
      type: 'list',
      items: [
        'Identificadores del dispositivo: modelo, sistema operativo y versión, identificador único de instalación (push token).',
        'Datos técnicos: versión de la App, idioma, zona horaria, dirección IP (para seguridad y detección de fraude).',
        'Datos de uso: pantallas visitadas, eventos de interacción (sin incluir montos específicos ni datos sensibles).',
        'Datos de diagnóstico: registros de errores y fallos para mejorar la estabilidad.',
      ],
    },

    { type: 'heading', level: 2, text: 'No recolectamos ni almacenamos' },
    {
      type: 'list',
      items: [
        'Lista de contactos del dispositivo.',
        'Fotos, archivos o medios del dispositivo (salvo documentos que el Asociado decida compartir con soporte).',
        'Ubicación precisa en tiempo real.',
        'Datos biométricos en nuestros servidores (se almacenan únicamente en el enclave seguro del dispositivo).',
      ],
    },

    { type: 'heading', level: 1, text: '3. Finalidades del tratamiento' },
    { type: 'paragraph', text: 'Usamos los datos personales para:' },
    {
      type: 'list',
      items: [
        'Autenticar al Asociado y garantizar el acceso seguro a la App.',
        'Mostrar los datos de sus productos y movimientos con la Cooperativa.',
        'Enviar notificaciones operativas sobre sus cuentas y avisos institucionales.',
        'Cumplir con obligaciones legales y regulatorias (prevención de lavado, reportes a autoridades cuando aplique).',
        'Prevenir, detectar y responder a actividades fraudulentas o ilícitas.',
        'Mejorar el rendimiento, usabilidad y estabilidad de la App mediante análisis agregado y anonimizado.',
        'Atender consultas, reclamos y solicitudes del Asociado.',
      ],
    },

    { type: 'heading', level: 1, text: '4. Base legitimadora' },
    { type: 'paragraph', text: 'El tratamiento se realiza con base en:' },
    {
      type: 'list',
      items: [
        'Ejecución del vínculo cooperativo: tratamiento necesario para prestar el servicio al Asociado.',
        'Cumplimiento de obligaciones legales: reportes a la Superintendencia, a la UIF y a la autoridad tributaria conforme a la legislación aplicable.',
        'Consentimiento del Asociado: para finalidades no esenciales (notificaciones promocionales, analítica opcional).',
        'Interés legítimo: seguridad, prevención del fraude y mejora técnica.',
      ],
    },

    { type: 'heading', level: 1, text: '5. Encargados del tratamiento (terceros con acceso a datos)' },
    {
      type: 'paragraph',
      text: 'Compartimos datos con terceros proveedores que actúan como encargados del tratamiento, exclusivamente para prestar servicios necesarios a la App:',
    },
    {
      type: 'list',
      items: [
        'Supabase, Inc. (Estados Unidos) — base de datos, autenticación, almacenamiento. Cifrado en tránsito y reposo.',
        'Cloudflare, Inc. (Estados Unidos) — CDN, DNS, WAF, protección anti-DDoS. Cifrado TLS.',
        'Apple, Inc. (Estados Unidos) — distribución de la App vía App Store.',
        'Google LLC (Estados Unidos) — distribución vía Google Play y servicios de notificaciones Firebase.',
        'Expo, Inc. (Estados Unidos) — build y despliegue de la App.',
        'Functional Software, Inc. (Sentry, Estados Unidos) — monitoreo de errores con anonimización parcial.',
      ],
    },

    { type: 'heading', level: 1, text: '6. Plazos de conservación' },
    {
      type: 'list',
      items: [
        'Datos de sesión y autenticación: hasta 90 días después del último acceso.',
        'Datos de productos y movimientos: por el tiempo del vínculo cooperativo más los plazos obligatorios de conservación contable y tributaria (5 años mínimo).',
        'Logs técnicos y de diagnóstico: hasta 180 días.',
        'Registros de auditoría de seguridad: hasta 2 años.',
        'Push tokens: hasta 30 días de inactividad o hasta que el Asociado revoque el acceso en su dispositivo.',
      ],
    },

    { type: 'heading', level: 1, text: '7. Derechos del Asociado' },
    {
      type: 'paragraph',
      text: 'El Asociado puede ejercer en cualquier momento los siguientes derechos sobre sus datos personales:',
    },
    {
      type: 'list',
      items: [
        'Acceso: solicitar información sobre qué datos tenemos sobre él.',
        'Rectificación: solicitar la corrección de datos inexactos.',
        'Supresión: solicitar la eliminación de datos cuando ya no sean necesarios (sujeto a los plazos legales de conservación).',
        'Oposición: oponerse al tratamiento para finalidades no esenciales.',
        'Portabilidad: solicitar una copia de sus datos en formato estructurado.',
        'Revocar consentimientos previamente otorgados para finalidades opcionales.',
      ],
    },
    {
      type: 'paragraph',
      text: `Las solicitudes se pueden enviar a ${COOPERATIVA_INFO.privacyEmail}. La Cooperativa responderá en un plazo razonable no mayor a 30 días corridos.`,
    },
    {
      type: 'paragraph',
      text: 'El Asociado también podrá presentar reclamos ante la autoridad de protección de datos que resulte competente conforme a la legislación vigente al momento del reclamo.',
    },

    { type: 'heading', level: 1, text: '8. Seguridad' },
    {
      type: 'paragraph',
      text: 'Aplicamos medidas técnicas y organizativas razonables para proteger los datos, incluyendo:',
    },
    {
      type: 'list',
      items: [
        'Cifrado TLS en tránsito y AES-256 en reposo.',
        'Autenticación multifactor opcional (biometría).',
        'Control de acceso basado en roles (RLS en base de datos).',
        'Registro de auditoría de accesos administrativos.',
        'Pruebas de seguridad periódicas y monitoreo continuo.',
        'Capacitación del personal con acceso a datos personales.',
      ],
    },
    {
      type: 'paragraph',
      text: 'A pesar de estas medidas, ninguna plataforma es 100% inmune a incidentes. En caso de un incidente que comprometa datos personales del Asociado, notificaremos al Asociado y, cuando corresponda, a las autoridades competentes dentro de los plazos legales.',
    },

    { type: 'heading', level: 1, text: '9. Datos de menores de edad' },
    {
      type: 'paragraph',
      text: 'La App está dirigida a personas mayores de 18 años. No recolectamos intencionalmente datos de menores. Si detectamos que hemos recolectado datos de un menor sin el consentimiento del representante legal, los eliminaremos.',
    },

    { type: 'heading', level: 1, text: '10. Cookies y tecnologías similares' },
    {
      type: 'paragraph',
      text: 'La App no usa cookies HTTP (no es un navegador web). Usa tecnologías equivalentes (almacenamiento local seguro del sistema operativo, tokens de autenticación) estrictamente necesarias para el funcionamiento. No se usan tecnologías de seguimiento publicitario de terceros.',
    },

    { type: 'heading', level: 1, text: '11. Cambios a esta política' },
    {
      type: 'paragraph',
      text: `La Cooperativa podrá actualizar esta Política. Las modificaciones sustantivas serán notificadas al Asociado con antelación razonable a través de la App y/o correo registrado. La versión vigente siempre estará disponible en la App, en la sección "Perfil → Términos y privacidad" y en ${COOPERATIVA_INFO.web}/privacidad.`,
    },

    { type: 'heading', level: 1, text: '12. Contacto' },
    {
      type: 'list',
      items: [
        `Correo: ${COOPERATIVA_INFO.privacyEmail}`,
        `Domicilio: ${COOPERATIVA_INFO.address}`,
      ],
    },
  ],
}

export const docs = { terms, privacy } as const
export type DocKind = keyof typeof docs
